import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  MenuBook,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { List, ListItem, ListItemIcon, ListItemText, Popover, InputAdornment, TableSortLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  getAllCourses,
  createCourse,
  updateCourse,
  deactivateCourse,
  activateCourse,
  archiveCourse,
  getAdminsForCourse,
  CourseDTO,
  getCurriculumSubjects,
  getCurriculums,
  getAllCountries,
  CountryDTO,
  CurriculumDTO,
  getCoreSubjects,
  scottishCurriculumManagementApi,
} from '../services/adminCourseService';
import { config } from '../config/environment';
import {
  getAllSystemAdmins,
  assignCoursesToAdmin,
  removeCourseAssignment,
  SystemAdminDTO,
  AssignCourseRequest,
} from '../services/systemAdminService';
import { getBoards, AdminBoardDTO } from '../services/adminLocationService';
import courseCodeService from '../services/courseCodeService';
import authService from '../services/authService';
import abacusService, { AbacusBootstrapResult } from '../services/abacusService';
import { CodeType } from '../types';

type CourseCreationMode = 'curriculum' | 'standalone';

function formatReportList(values?: string[]): string {
  if (!values || values.length === 0) {
    return 'none';
  }
  const shown = values.slice(0, 8).join(', ');
  return values.length > 8 ? `${shown}, +${values.length - 8} more` : shown;
}

const AdminCourseManagement: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<CourseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bootstrapMessage, setBootstrapMessage] = useState<string | null>(null);
  const [bootstrapReport, setBootstrapReport] = useState<AbacusBootstrapResult | null>(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [templateTrack, setTemplateTrack] = useState<'JUNIOR' | 'SENIOR'>('JUNIOR');
  const [competitionTitle, setCompetitionTitle] = useState('Junior sprint');
  const [competitionId, setCompetitionId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<CourseDTO | null>(null);
  const [formData, setFormData] = useState<CourseDTO>({
    name: '',
    description: '',
    courseType: 'SECONDARY_SCHOOL',
    status: 'ACTIVE',
    registrationMethod: 'CODE',
    maxStudents: 0,
    durationWeeks: 0,
    difficultyLevel: '',
    prerequisites: '',
    curriculumAlignment: '',
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCourse, setSelectedCourse] = useState<CourseDTO | null>(null);
  const [adminsDialogOpen, setAdminsDialogOpen] = useState(false);
  const [courseAdmins, setCourseAdmins] = useState<SystemAdminDTO[]>([]);
  const [assignAdminDialogOpen, setAssignAdminDialogOpen] = useState(false);
  const [availableAdmins, setAvailableAdmins] = useState<SystemAdminDTO[]>([]);
  const [selectedAdminIds, setSelectedAdminIds] = useState<number[]>([]);
  const [curriculumSubjects, setCurriculumSubjects] = useState<
    { id: number; code: string; name: string; description?: string; coreSubjectId?: number; boardId?: number }[]
  >([]);
  const [coreSubjects, setCoreSubjects] = useState<{ id: number; code: string; name: string }[]>([]);

  const [countries, setCountries] = useState<CountryDTO[]>([]);
  const [allBoards, setAllBoards] = useState<AdminBoardDTO[]>([]);
  const [allCurriculums, setAllCurriculums] = useState<CurriculumDTO[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number | ''>('');
  const [selectedBoardId, setSelectedBoardId] = useState<number | ''>('');
  const [creationMode, setCreationMode] = useState<CourseCreationMode | null>(null);

  const scottishBaseUrl = config.scottishCurriculumApiUrl || 'http://localhost:8082';

  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof CourseDTO>('name');
  const [globalFilter, setGlobalFilter] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<keyof CourseDTO | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [enrollmentCodeDialogOpen, setEnrollmentCodeDialogOpen] = useState(false);
  const [lastGeneratedCode, setLastGeneratedCode] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      loadCountries();
      const [boardsData, csData, coreData] = await Promise.all([
        getBoards().catch(() => [] as AdminBoardDTO[]),
        getCurriculumSubjects().catch(() => [] as any[]),
        getCoreSubjects().catch(() => [] as any[]),
      ]);
      setAllBoards(boardsData);
      setCurriculumSubjects(csData);
      setCoreSubjects(coreData);
      await loadCourses(boardsData, csData, coreData);
    };
    init();
  }, []);

  const loadAllBoards = async () => {
    try {
      const data = await getBoards();
      setAllBoards(data);
      return data;
    } catch (err) {
      console.error('Failed to load boards', err);
      return [];
    }
  };

  const loadCourses = async (
    boardsRef?: AdminBoardDTO[],
    csRef?: { id: number; code: string; name: string; coreSubjectId?: number; boardId?: number }[],
    coreRef?: { id: number; code: string; name: string }[],
  ) => {
    const boards = boardsRef ?? allBoards;
    const subjects = csRef ?? curriculumSubjects;
    const cores = coreRef ?? coreSubjects;
    try {
      setLoading(true);
      const data = await getAllCourses();

      let allCurricula: CurriculumDTO[] = [];
      try { allCurricula = await getCurriculums(); } catch { /* ignore */ }

      const enriched = await Promise.all(data.map(async (course) => {
        const cs = course.curriculumSubjectId ? subjects.find(s => s.id === course.curriculumSubjectId) : undefined;
        const coreSubject = cs?.coreSubjectId ? cores.find(c => c.id === cs.coreSubjectId) : undefined;
        const board = cs?.boardId ? boards.find(b => b.id === cs.boardId) : undefined;
        const coreCurriculum = course.curriculumId ? allCurricula.find(c => c.id === course.curriculumId) : undefined;

        let scottishCurriculumId: number | undefined;
        let scottishLevel: string | undefined;
        if (course.curriculumId && board?.boardType?.toUpperCase() === 'SCOTTISH') {
          try {
            const res = await scottishCurriculumManagementApi.getCurriculumByCoreId(scottishBaseUrl, course.curriculumId);
            if (res.data?.success && res.data.data) {
              scottishCurriculumId = res.data.data.id;
              scottishLevel = res.data.data.level;
            }
          } catch { /* may not exist */ }
        }

        return {
          ...course,
          curriculumSubjectName: cs?.name,
          coreSubjectName: coreSubject?.name,
          boardName: board?.name,
          coreCurriculumVersion: coreCurriculum?.version,
          scottishCurriculumId,
          scottishLevel,
        };
      }));
      setCourses(enriched);
    } catch (err: any) {
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const loadCurriculumSubjects = async () => {
    try {
      const subjects = await getCurriculumSubjects();
      setCurriculumSubjects(subjects);
    } catch (err) {
      console.error('Failed to load curriculum subjects', err);
    }
  };

  const loadCountries = async () => {
    try {
      const data = await getAllCountries();
      setCountries(data);
    } catch (err) {
      console.error('Failed to load countries', err);
    }
  };

  const loadAllCurriculums = async () => {
    try {
      const data = await getCurriculums();
      setAllCurriculums(data);
      return data;
    } catch (err) {
      console.error('Failed to load course curricula', err);
      setAllCurriculums([]);
      return [] as CurriculumDTO[];
    }
  };

  const prefillCurriculumCascade = (
    course: CourseDTO,
    curricula: CurriculumDTO[],
    boards: AdminBoardDTO[],
    subjects: { id: number; boardId?: number }[],
  ) => {
    if (course.curriculumId) {
      const matching = curricula.find((c) => c.id === course.curriculumId);
      if (matching) {
        setSelectedCountryId(matching.countryId);
        setSelectedBoardId(matching.boardId);
        return;
      }
    }
    if (course.curriculumSubjectId) {
      const subject = subjects.find((s) => s.id === course.curriculumSubjectId);
      const board = subject?.boardId ? boards.find((b) => b.id === subject.boardId) : undefined;
      if (board) {
        setSelectedCountryId(board.countryId);
        setSelectedBoardId(board.id);
        return;
      }
    }
    setSelectedCountryId('');
    setSelectedBoardId('');
  };

  const handleOpenDialog = async (course?: CourseDTO, mode: CourseCreationMode = 'curriculum') => {
    const curricula = await loadAllCurriculums();
    if (course) {
      setEditingCourse(course);
      setCreationMode(null);
      setFormData(course);
      prefillCurriculumCascade(course, curricula, allBoards, curriculumSubjects);
    } else {
      setEditingCourse(null);
      setCreationMode(mode);
      setFormData({
        name: '',
        description: '',
        courseType: mode === 'standalone' ? 'ABACUS_MATHS' : 'SECONDARY_SCHOOL',
        status: mode === 'standalone' ? 'COMING_SOON' : 'ACTIVE',
        registrationMethod: mode === 'standalone' ? 'OPEN' : 'CODE',
        abacusVariant: mode === 'standalone' ? 'JUNIOR' : undefined,
        maxStudents: 0,
        durationWeeks: 0,
        difficultyLevel: '',
        prerequisites: '',
        curriculumAlignment: '',
        curriculumSubjectId: undefined,
        curriculumId: undefined,
      });
      setSelectedCountryId('');
      setSelectedBoardId('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCourse(null);
    setCreationMode(null);
    setSelectedCountryId('');
    setSelectedBoardId('');
  };

  const showCurriculumFields =
    creationMode === 'curriculum'
    || (editingCourse !== null && Boolean(editingCourse.curriculumId || editingCourse.curriculumSubjectId));

  const filteredBoards = selectedCountryId
    ? allBoards.filter((b) => b.countryId === Number(selectedCountryId))
    : [];
  const filteredSubjects = selectedBoardId
    ? curriculumSubjects.filter((s) => s.boardId === Number(selectedBoardId))
    : [];
  const filteredCurricula =
    selectedCountryId && selectedBoardId && formData.curriculumSubjectId
      ? allCurriculums.filter(
          (c) =>
            c.countryId === Number(selectedCountryId) &&
            c.boardId === Number(selectedBoardId) &&
            c.curriculumSubjectId === formData.curriculumSubjectId,
        )
      : [];

  const handleSubmit = async () => {
    try {
      let payload: CourseDTO = { ...formData };
      if (showCurriculumFields) {
        if (!formData.curriculumId) {
          setError('Please select a course curriculum');
          return;
        }
        const selected = allCurriculums.find((c) => c.id === formData.curriculumId);
        if (!selected) {
          setError('Selected course curriculum is invalid');
          return;
        }
        payload = {
          ...formData,
          curriculumId: selected.id,
          curriculumSubjectId: selected.curriculumSubjectId,
        };
      }
      if (editingCourse?.id) {
        await updateCourse(editingCourse.id, payload);
      } else {
        await createCourse(payload);
      }
      handleCloseDialog();
      loadCourses();
    } catch (err: any) {
      setError(err.message || 'Failed to save course');
    }
  };

  const handleDelete = async () => {
    if (!selectedCourse?.id) return;
    try {
      await deactivateCourse(selectedCourse.id);
      setMenuAnchor(null);
      setSelectedCourse(null);
      loadCourses();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate course');
    }
  };

  const handleActivate = async () => {
    if (!selectedCourse?.id) return;
    try {
      await activateCourse(selectedCourse.id);
      setMenuAnchor(null);
      setSelectedCourse(null);
      loadCourses();
    } catch (err: any) {
      setError(err.message || 'Failed to activate course');
      setMenuAnchor(null);
    }
  };

  const handleArchive = async () => {
    if (!selectedCourse?.id) return;
    try {
      await archiveCourse(selectedCourse.id);
      setMenuAnchor(null);
      setSelectedCourse(null);
      loadCourses();
    } catch (err: any) {
      setError(err.message || 'Failed to archive course');
      setMenuAnchor(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, course: CourseDTO) => {
    setMenuAnchor(event.currentTarget);
    setSelectedCourse(course);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCourse(null);
  };

  const handleViewAdmins = async (course: CourseDTO) => {
    try {
      setSelectedCourse(course);
      const admins = await getAdminsForCourse(course.id!);
      setCourseAdmins(admins);
      setAdminsDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load admins for course');
    }
  };

  const handleAssignAdmins = async (course: CourseDTO) => {
    try {
      setSelectedCourse(course);
      const allAdmins = await getAllSystemAdmins();
      const currentAdmins = await getAdminsForCourse(course.id!);
      const currentAdminIds = currentAdmins.map(a => a.id);

      // Filter out admins already assigned
      const available = allAdmins.filter(a => !currentAdminIds.includes(a.id));
      setAvailableAdmins(available);
      setSelectedAdminIds([]);
      setAssignAdminDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load admins');
    }
  };

  const handleAssignAdminsSubmit = async () => {
    if (!selectedCourse?.id || selectedAdminIds.length === 0) return;

    try {
      for (const adminId of selectedAdminIds) {
        const request: AssignCourseRequest = { courseIds: [selectedCourse.id] };
        await assignCoursesToAdmin(adminId, request);
      }
      setAssignAdminDialogOpen(false);
      setSelectedCourse(null);
      setError(null);
      // Refresh the admins list if dialog is open
      if (adminsDialogOpen && selectedCourse) {
        const admins = await getAdminsForCourse(selectedCourse.id);
        setCourseAdmins(admins);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to assign admins');
    }
  };

  const handleRemoveAdmin = async (adminId: number) => {
    if (!selectedCourse?.id) return;

    try {
      await removeCourseAssignment(adminId, selectedCourse.id);
      // Refresh the admins list
      const admins = await getAdminsForCourse(selectedCourse.id);
      setCourseAdmins(admins);
    } catch (err: any) {
      setError(err.message || 'Failed to remove admin from course');
    }
  };

  // --- Sorting & Filtering Logic ---

  const handleRequestSort = (property: keyof CourseDTO) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, column: keyof CourseDTO) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleColumnFilterChange = (value: string) => {
    if (!activeFilterColumn) return;

    const currentFilters = columnFilters[activeFilterColumn] || [];
    let newFilters: string[];

    if (currentFilters.includes(value)) {
      newFilters = currentFilters.filter(item => item !== value);
    } else {
      newFilters = [...currentFilters, value];
    }

    setColumnFilters({
      ...columnFilters,
      [activeFilterColumn]: newFilters,
    });
  };

  const clearColumnFilter = (column: keyof CourseDTO) => {
    const newFilters = { ...columnFilters };
    delete newFilters[column];
    setColumnFilters(newFilters);
  };

  const getUniqueValues = (column: keyof CourseDTO) => {
    const values = courses.map(course => String(course[column] || '')).filter(Boolean);
    return Array.from(new Set(values)).sort();
  };

  const filteredCourses = courses.filter((course) => {
    // 1. Global Filter
    if (globalFilter) {
      const searchStr = globalFilter.toLowerCase();
      const matchesGlobal = Object.values(course).some(val =>
        String(val).toLowerCase().includes(searchStr)
      );
      if (!matchesGlobal) return false;
    }

    // 2. Column Filters
    for (const [key, selectedValues] of Object.entries(columnFilters)) {
      if (selectedValues && selectedValues.length > 0) {
        // @ts-ignore
        const cellValue = String(course[key] || '');
        if (!selectedValues.includes(cellValue)) {
          return false;
        }
      }
    }

    return true;
  });

  const sortedAndFilteredCourses = React.useMemo(() => {
    return [...filteredCourses].sort((a, b) => {
      // @ts-ignore
      const aValue = a[orderBy] || '';
      // @ts-ignore
      const bValue = b[orderBy] || '';

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return order === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredCourses, order, orderBy]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Course Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(undefined, 'curriculum')}
          >
            Create from Curriculum
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(undefined, 'standalone')}
          >
            Create without Curriculum
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', maxWidth: 280 }}>
            Specialized courses (e.g. Abacus Maths) without national curriculum alignment — use this path, not Create from Curriculum.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="abacus-template-track-label">Track</InputLabel>
            <Select
              labelId="abacus-template-track-label"
              label="Track"
              value={templateTrack}
              data-testid="abacus-template-track"
              onChange={(e) => setTemplateTrack(e.target.value as 'JUNIOR' | 'SENIOR')}
            >
              <MenuItem value="JUNIOR">Junior</MenuItem>
              <MenuItem value="SENIOR">Senior</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            data-testid="create-abacus-from-template"
            disabled={bootstrapping}
            onClick={async () => {
              try {
                setBootstrapping(true);
                setBootstrapMessage(null);
                setError(null);
                const result = await abacusService.runAdminBootstrap({ track: templateTrack });
                setBootstrapReport(result);
                const created = result.topicsCreated ?? 0;
                setBootstrapMessage(
                  result.courseCreated
                    ? `Created course ${result.courseId}. Topics added: ${created}.`
                    : `Merged course ${result.courseId}. Topics added: ${created}.`
                );
                await loadCourses();
              } catch (e: unknown) {
                const response = (e as { response?: { data?: { message?: string; data?: AbacusBootstrapResult } } })
                  .response?.data;
                if (response?.data?.validationReport) {
                  setBootstrapReport(response.data);
                }
                setError(response?.message || (e instanceof Error ? e.message : 'Abacus bootstrap failed'));
              } finally {
                setBootstrapping(false);
              }
            }}
          >
            {bootstrapping ? 'Bootstrapping…' : 'Create Abacus Maths from template'}
          </Button>
        </Box>
      </Box>

      {bootstrapReport?.validationReport && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="abacus-bootstrap-report">
          <div>
            Version {bootstrapReport.syllabusVersion || 'unknown'}. Topics added:{' '}
            {bootstrapReport.topicsCreated ?? 0}.
          </div>
          <div>Missing content: {formatReportList(bootstrapReport.validationReport.missingContent)}</div>
          <div>Missing quiz config: {formatReportList(bootstrapReport.validationReport.missingQuizConfig)}</div>
          <div>Unknown skill tags: {formatReportList(bootstrapReport.validationReport.unknownSkillTags)}</div>
          <div>Orphan nodes: {formatReportList(bootstrapReport.validationReport.orphanNodes)}</div>
          <div>Missing pools: {formatReportList(bootstrapReport.validationReport.missingPools)}</div>
        </Alert>
      )}
      {bootstrapMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setBootstrapMessage(null)}>
          {bootstrapMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="abacus-competition-form">
        <Typography fontWeight={700} sx={{ mb: 1 }}>Schedule an Abacus competition</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Title"
            value={competitionTitle}
            data-testid="abacus-competition-title"
            onChange={(event) => setCompetitionTitle(event.target.value)}
          />
          <Button
            variant="contained"
            data-testid="abacus-competition-create"
            onClick={() => {
              const start = new Date();
              const end = new Date(start.getTime() + 20 * 60 * 1000);
              const stamp = (value: Date) => value.toISOString().replace(/\.\d{3}Z$/, '');
              void abacusService.scheduleCompetition({
                title: competitionTitle,
                levelBand: 'JUNIOR_1_3',
                skillTags: 'ADD_SUB_1_99',
                startAt: stamp(start),
                endAt: stamp(end),
                poolMode: 'TIMED_DRILL',
              }).then((created) => setCompetitionId(created.id))
                .catch(() => setError('Could not schedule the competition.'));
            }}
          >
            Create competition
          </Button>
        </Stack>
        {competitionId != null && (
          <Alert severity="success" sx={{ mt: 1 }} data-testid="abacus-competition-created">
            Competition {competitionId} is open for 20 minutes.
          </Alert>
        )}
      </Paper>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search..."
          size="small"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        {/* Filter Chips Display could go here */}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {[
                { id: 'name', label: 'Name' },
                { id: 'id', label: 'ID' },
                { id: 'courseType', label: 'Type' },
                { id: 'status', label: 'Status' },
                { id: 'registrationMethod', label: 'Registration' },
                { id: 'boardName', label: 'Board' },
                { id: 'coreSubjectName', label: 'Core Subject' },
                { id: 'curriculumSubjectName', label: 'Curriculum Subject' },
                { id: 'curriculumId', label: 'Core Curriculum' },
                { id: 'scottishCurriculumId', label: 'Scottish Curriculum' },
                { id: 'maxStudents', label: 'Max Students' },
              ].map((headCell) => (
                <TableCell key={headCell.id}>
                  <Box display="flex" alignItems="center">
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id as keyof CourseDTO)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, headCell.id as keyof CourseDTO)}
                      color={columnFilters[headCell.id]?.length ? 'primary' : 'default'}
                    >
                      <FilterIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAndFilteredCourses.map((course) => (
              <TableRow key={course.id}>
                <TableCell>{course.name}</TableCell>
                <TableCell>{course.id}</TableCell>
                <TableCell>
                  <Chip label={course.courseType} size="small" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={course.status}
                    color={course.status === 'ACTIVE' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip label={course.registrationMethod || 'CODE'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{course.boardName || '—'}</TableCell>
                <TableCell>{course.coreSubjectName || '—'}</TableCell>
                <TableCell>{course.curriculumSubjectName ? `${course.curriculumSubjectName} (ID: ${course.curriculumSubjectId})` : '—'}</TableCell>
                <TableCell>{course.curriculumId ? `ID: ${course.curriculumId}${course.coreCurriculumVersion ? ` (${course.coreCurriculumVersion})` : ''}` : '—'}</TableCell>
                <TableCell>{course.scottishCurriculumId ? `ID: ${course.scottishCurriculumId}${course.scottishLevel ? ` (${course.scottishLevel.replace(/_/g, ' ')})` : ''}` : '—'}</TableCell>
                <TableCell>{course.maxStudents || 'N/A'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, course)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {sortedAndFilteredCourses.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    No courses found matching criteria.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Column Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200, maxHeight: 300, overflowY: 'auto' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">Filter by {activeFilterColumn}</Typography>
            {(columnFilters[activeFilterColumn || '']?.length || 0) > 0 && (
              <IconButton size="small" onClick={() => activeFilterColumn && clearColumnFilter(activeFilterColumn)}>
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <List dense>
            {activeFilterColumn && getUniqueValues(activeFilterColumn).map((value) => {
              const isChecked = (columnFilters[activeFilterColumn] || []).includes(value);
              return (
                <ListItem key={value} button onClick={() => handleColumnFilterChange(value)}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={value} />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Popover>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedCourse?.id) navigate(`/admin/courses/${selectedCourse.id}/dashboard`);
          handleMenuClose();
        }}>
          <DashboardIcon sx={{ mr: 1 }} /> Performance Dashboard
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCourse?.id) navigate(`/admin/courses/${selectedCourse.id}/content`);
          handleMenuClose();
        }}>
          <MenuBook sx={{ mr: 1 }} /> Views and Topic Management
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCourse) handleAssignAdmins(selectedCourse);
          handleMenuClose();
        }}>
          <AddIcon sx={{ mr: 1 }} /> Assign Admins
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCourse) handleViewAdmins(selectedCourse);
          handleMenuClose();
        }}>
          <PeopleIcon sx={{ mr: 1 }} /> View Assigned Admins
        </MenuItem>
        <MenuItem
          onClick={async () => {
            const course = selectedCourse;
            handleMenuClose();
            if (!course?.id) return;
            const admin = authService.getCurrentUser();
            if (!admin?.id) {
              setError('You must be logged in to generate a code.');
              return;
            }
            try {
              const cc = await courseCodeService.generateCourseCode({
                courseId: course.id,
                codeType: CodeType.SCHOOL,
                createdByUserId: admin.id,
              });
              setLastGeneratedCode(cc.code);
              setEnrollmentCodeDialogOpen(true);
            } catch (e: unknown) {
              const msg = e instanceof Error ? e.message : 'Failed to generate code';
              setError(msg);
            }
          }}
          disabled={
            !selectedCourse || String(selectedCourse.registrationMethod || 'CODE').toUpperCase() !== 'CODE'
          }
        >
          <VpnKeyIcon sx={{ mr: 1 }} /> Generate enrollment code
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCourse) handleOpenDialog(selectedCourse);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleActivate}
          disabled={selectedCourse?.status === 'ACTIVE'}>
          <AddIcon sx={{ mr: 1 }} /> Activate
        </MenuItem>
        <MenuItem onClick={handleArchive}
          disabled={selectedCourse?.status === 'ARCHIVED'}>
          <DeleteIcon sx={{ mr: 1, color: 'warning.main' }} /> Archive
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon sx={{ mr: 1 }} /> Deactivate
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCourse
            ? 'Edit Course'
            : creationMode === 'curriculum'
              ? 'Create Course from Curriculum'
              : 'Create Course'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Course Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
            {creationMode === 'standalone' ? (
              <TextField
                fullWidth
                label="Course Type"
                value="Abacus Maths"
                InputProps={{ readOnly: true }}
                helperText="071 MVP: standalone create supports Abacus Maths only."
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Course Type</InputLabel>
                <Select
                  value={formData.courseType || 'SECONDARY_SCHOOL'}
                  label="Course Type"
                  onChange={(e) =>
                    setFormData({ ...formData, courseType: e.target.value })
                  }
                >
                  <MenuItem value="SECONDARY_SCHOOL">Secondary School</MenuItem>
                  <MenuItem value="PRIMARY_SCHOOL">Primary School</MenuItem>
                  {(formData.courseType === 'ABACUS_MATHS'
                    || editingCourse?.courseType === 'ABACUS_MATHS') && (
                    <MenuItem value="ABACUS_MATHS">Abacus Maths</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || (creationMode === 'standalone' ? 'COMING_SOON' : 'ACTIVE')}
                label="Status"
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="COMING_SOON">Coming Soon</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="ARCHIVED">Archived</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Registration Method</InputLabel>
              <Select
                value={formData.registrationMethod || 'CODE'}
                label="Registration Method"
                onChange={(e) =>
                  setFormData({ ...formData, registrationMethod: e.target.value })
                }
              >
                <MenuItem value="CODE">Code — students enter an enrollment code</MenuItem>
                <MenuItem value="OPEN">Open — anyone with the link can enroll</MenuItem>
                <MenuItem value="INVITE">Invite — admin-assigned only</MenuItem>
              </Select>
            </FormControl>
            {formData.courseType === 'ABACUS_MATHS' && (
              <FormControl fullWidth required>
                <InputLabel>Abacus variant</InputLabel>
                <Select
                  value={formData.abacusVariant || 'JUNIOR'}
                  label="Abacus variant"
                  onChange={(e) =>
                    setFormData({ ...formData, abacusVariant: e.target.value })
                  }
                >
                  <MenuItem value="JUNIOR">Junior (Levels 1–11)</MenuItem>
                  <MenuItem value="SENIOR">Senior (Levels 1A–8A)</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              label="Max Students"
              fullWidth
              type="number"
              value={formData.maxStudents || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxStudents: parseInt(e.target.value) || 0,
                })
              }
            />
            <TextField
              label="Duration (Weeks)"
              fullWidth
              type="number"
              value={formData.durationWeeks || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  durationWeeks: parseInt(e.target.value) || 0,
                })
              }
            />
            <TextField
              label="Difficulty Level"
              fullWidth
              value={formData.difficultyLevel || ''}
              onChange={(e) =>
                setFormData({ ...formData, difficultyLevel: e.target.value })
              }
            />
            {showCurriculumFields && (
              <>
                <FormControl fullWidth required>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={selectedCountryId}
                    label="Country"
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedCountryId(value === '' ? '' : Number(value));
                      setSelectedBoardId('');
                      setFormData({
                        ...formData,
                        curriculumSubjectId: undefined,
                        curriculumId: undefined,
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Select country</em>
                    </MenuItem>
                    {countries.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth required disabled={!selectedCountryId}>
                  <InputLabel>Board</InputLabel>
                  <Select
                    value={selectedBoardId}
                    label="Board"
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedBoardId(value === '' ? '' : Number(value));
                      setFormData({
                        ...formData,
                        curriculumSubjectId: undefined,
                        curriculumId: undefined,
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Select board</em>
                    </MenuItem>
                    {filteredBoards.map((board) => (
                      <MenuItem key={board.id} value={board.id}>
                        {board.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth required disabled={!selectedBoardId}>
                  <InputLabel>Curriculum Subject</InputLabel>
                  <Select
                    value={formData.curriculumSubjectId ?? ''}
                    label="Curriculum Subject"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        curriculumSubjectId: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                        curriculumId: undefined,
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>Select curriculum subject</em>
                    </MenuItem>
                    {filteredSubjects.map((subject) => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.code} - {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  required
                  disabled={!selectedCountryId || !selectedBoardId || !formData.curriculumSubjectId}
                >
                  <InputLabel>Course Curriculum</InputLabel>
                  <Select
                    value={formData.curriculumId ?? ''}
                    label="Course Curriculum"
                    onChange={(e) => {
                      const curriculumId = e.target.value ? Number(e.target.value) : undefined;
                      const selected = curriculumId
                        ? allCurriculums.find((c) => c.id === curriculumId)
                        : undefined;
                      setFormData({
                        ...formData,
                        curriculumId,
                        curriculumSubjectId: selected?.curriculumSubjectId ?? formData.curriculumSubjectId,
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Select course curriculum</em>
                    </MenuItem>
                    {filteredCurricula.map((curr) => (
                      <MenuItem key={curr.id} value={curr.id}>
                        {curr.version}
                        {curr.level ? ` — ${curr.level}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCourse ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Assigned Admins Dialog */}
      <Dialog open={adminsDialogOpen} onClose={() => setAdminsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Admins Assigned to: {selectedCourse?.name}
        </DialogTitle>
        <DialogContent>
          {courseAdmins.length === 0 ? (
            <Typography color="text.secondary">
              No admins assigned to this course.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {courseAdmins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell>
                        {admin.firstName} {admin.lastName}
                      </TableCell>
                      <TableCell>{admin.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={admin.isSuperAdmin ? 'Super Admin' : 'Admin'}
                          size="small"
                          color={admin.isSuperAdmin ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveAdmin(admin.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminsDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setAdminsDialogOpen(false);
              if (selectedCourse) handleAssignAdmins(selectedCourse);
            }}
          >
            Assign More Admins
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Admins Dialog */}
      <Dialog open={assignAdminDialogOpen} onClose={() => setAssignAdminDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Admins to: {selectedCourse?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {availableAdmins.length === 0 ? (
              <Typography color="text.secondary">
                All admins are already assigned to this course.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {availableAdmins.map((admin) => (
                  <FormControlLabel
                    key={admin.id}
                    control={
                      <Checkbox
                        checked={selectedAdminIds.includes(admin.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAdminIds([...selectedAdminIds, admin.id]);
                          } else {
                            setSelectedAdminIds(selectedAdminIds.filter(id => id !== admin.id));
                          }
                        }}
                      />
                    }
                    label={`${admin.firstName} ${admin.lastName} (${admin.email})`}
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignAdminDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignAdminsSubmit}
            disabled={selectedAdminIds.length === 0}
          >
            Assign Selected
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={enrollmentCodeDialogOpen} onClose={() => setEnrollmentCodeDialogOpen(false)}>
        <DialogTitle>Enrollment code</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            Share this code with students so they can join under <strong>Register with course code</strong>:
          </Typography>
          <Typography variant="h5" sx={{ mt: 2, fontFamily: 'monospace', letterSpacing: 2 }}>
            {lastGeneratedCode || '—'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollmentCodeDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminCourseManagement;

